import { useState } from 'react';
import { Link } from 'react-router-dom';
import AppHeader from '../../components/AppHeader';
import { authApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

export const MIN_PASSWORD_LENGTH = 12;

function messageFor(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } } };
  const detail = e.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (e.response?.status === 422) return `The new password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  if (e.response?.status === 429) return 'Too many attempts. Wait a minute, then try again.';
  return 'We could not change your password just now. Check your connection and try again.';
}

export default function ChangePassword() {
  const setTokens = useAuthStore((s) => s.setTokens);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (next.length < MIN_PASSWORD_LENGTH) {
      setError(`The new password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await authApi.changePassword(current, next);
      setTokens(data.access_token, data.refresh_token);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="mx-auto max-w-5xl px-4 py-12">
        <div className="max-w-sm">
          <h1 className="text-display text-ink">Change password</h1>
          <p className="mt-2 text-body text-ink-muted">
            Changing your password signs you out everywhere else. This browser stays signed in.
          </p>

          {done ? (
            <div role="status" className="mt-8 rounded-md border border-line bg-surface p-6">
              <p className="text-body text-ink">
                <span className="font-semibold">Password changed.</span> Use the new password next time you sign in.
              </p>
              <Link to="/" className="btn-secondary mt-4">
                Back to MerchantView
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-md border border-line bg-surface p-6" noValidate>
              {error && (
                <div role="alert" className="rounded-md border border-danger bg-danger-tint p-3 text-caption text-danger">
                  <span className="font-semibold">Password not changed. </span>
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="current-password" className="label">
                  Current password
                </label>
                <input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label htmlFor="new-password" className="label">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  aria-describedby="new-password-hint"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className="input"
                />
                <p id="new-password-hint" className="mt-1 text-caption text-ink-muted">
                  At least {MIN_PASSWORD_LENGTH} characters.
                </p>
              </div>

              <div>
                <label htmlFor="confirm-password" className="label">
                  New password again
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input"
                />
              </div>

              <button type="submit" className="btn-primary w-full" disabled={submitting || !current || !next || !confirm}>
                {submitting ? 'Changing password' : 'Change password'}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
